import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, Users, Mail, Calendar, CheckCircle, XCircle,
  Clock, Crown, Shield, Ban, RefreshCw, ChevronDown,
  ChevronUp, Search, Filter, Activity, Zap, MessageSquare,
  TrendingUp, Eye, ToggleLeft, ToggleRight, AlertCircle,
  Check, X, User, Database, Unlink,
} from 'lucide-react';
import { API_URL } from '../../config';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};
const fmtShort = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); }
  catch { return iso; }
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

// ── Badge Plan ────────────────────────────────────────────────────────────────
const PlanBadge = ({ isPro, disabled }) => {
  if (disabled) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gray-800 text-gray-400 border border-gray-700">
      <Ban className="w-3 h-3" /> DÉSACTIVÉ
    </span>
  );
  if (isPro) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-teal-500 to-emerald-500 text-white">
      <Crown className="w-3 h-3" /> PRO
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gray-700 text-gray-300 border border-gray-600">
      FREE
    </span>
  );
};

// ── Status OAuth Badge ────────────────────────────────────────────────────────
const OAuthBadge = ({ status }) => {
  const map = {
    connected: { color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30', icon: CheckCircle, label: 'Connecté' },
    approved:  { color: 'text-blue-400 bg-blue-400/10 border-blue-400/30',          icon: Check,       label: 'Approuvé' },
    pending:   { color: 'text-amber-400 bg-amber-400/10 border-amber-400/30',        icon: Clock,       label: 'En attente' },
    rejected:  { color: 'text-red-400 bg-red-400/10 border-red-400/30',             icon: XCircle,     label: 'Rejeté' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.color}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
export default function AssistantTeckiAAdmin() {
  const [tab, setTab]           = useState('users');
  const [users, setUsers]       = useState([]);
  const [oauths, setOauths]     = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [search, setSearch]     = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [toast, setToast]       = useState(null);
  const [dissociating, setDissociating] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Charger données ─────────────────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/assistant/users`, { headers: authHeaders() });
      const j = await r.json();
      setUsers(j.users || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadOAuths = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/assistant/oauth-requests`, { headers: authHeaders() });
      const j = await r.json();
      setOauths(j.requests || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/admin/assistant/stats`, { headers: authHeaders() });
      const j = await r.json();
      setGlobalStats(j.stats || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'users')  loadUsers();
    if (tab === 'oauth')  loadOAuths();
    if (tab === 'stats')  loadStats();
  }, [tab]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const setPro = async (userId, isPro) => {
    try {
      const r = await fetch(`${API_URL}/subscription/set-pro`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId, service: 'assistant', isPro, planName: isPro ? 'pro' : 'free' }),
      });
      const j = await r.json();
      if (j.success) {
        setUsers(prev => prev.map(u => u.userId === userId ? { ...u, isPro, planName: isPro ? 'pro' : 'free' } : u));
        showToast(isPro ? `✦ Utilisateur passé en PRO` : `Utilisateur repassé en Free`);
      }
    } catch (e) { showToast('Erreur réseau', 'error'); }
  };

  const setDisabled = async (userId, disabled) => {
    try {
      const r = await fetch(`${API_URL}/admin/assistant/user/disable`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId, disabled }),
      });
      const j = await r.json();
      if (j.success) {
        setUsers(prev => prev.map(u => u.userId === userId ? { ...u, disabled } : u));
        showToast(disabled ? 'Service désactivé' : 'Service réactivé', disabled ? 'warn' : 'success');
      }
    } catch (e) { showToast('Erreur réseau', 'error'); }
  };

  const approveOAuth = async (docId, action) => {
    try {
      const r = await fetch(`${API_URL}/admin/assistant/oauth/approve`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ docId, action }),
      });
      const j = await r.json();
      if (j.success) {
        setOauths(prev => prev.map(o => o.$id === docId ? { ...o, status: action === 'approve' ? 'approved' : 'rejected' } : o));
        showToast(action === 'approve' ? '✓ Demande approuvée' : 'Demande rejetée', action === 'approve' ? 'success' : 'warn');
      }
    } catch (e) { showToast('Erreur réseau', 'error'); }
  };

  // ── Dissocier compte Google ─────────────────────────────────────────────────
  const dissociateGoogle = async (o) => {
    if (!window.confirm(`Dissocier le compte Google "${o.email}" de l'utilisateur ${o.userName || o.userId} ?`)) return;
    setDissociating(o.$id);
    try {
      // Route dédiée qui gère tout : supprime grant + reset oauth_request
      const r = await fetch(`${API_URL}/admin/assistant/oauth/disconnect`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({
          userId:     o.userId,
          email:      o.email,
          oauthId:    o.$id,
          grantDocId: o.grantDocId || '',
        }),
      });
      const j = await r.json();
      if (j.success) {
        setOauths(prev => prev.map(x => x.$id === o.$id ? { ...x, status: 'rejected' } : x));
        showToast(`✓ Compte ${o.email} dissocié`, 'success');
      } else {
        showToast('Erreur dissociation : ' + (j.errors?.join(', ') || 'inconnue'), 'error');
      }
    } catch (e) {
      showToast('Erreur dissociation', 'error');
    } finally {
      setDissociating(null);
    }
  };

  // ── Filtres ──────────────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const matchSearch = !search || u.email?.toLowerCase().includes(search.toLowerCase()) || u.userId?.toLowerCase().includes(search.toLowerCase());
    const matchPlan   = filterPlan === 'all' || (filterPlan === 'pro' && u.isPro) || (filterPlan === 'free' && !u.isPro) || (filterPlan === 'disabled' && u.disabled);
    return matchSearch && matchPlan;
  });

  const pendingOAuths = oauths.filter(o => o.status === 'pending').length;

  return (
    <div className="space-y-6">
      {/* ── Toast ── */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl text-white font-semibold text-sm transition-all
          ${toast.type === 'error' ? 'bg-red-600' : toast.type === 'warn' ? 'bg-amber-600' : 'bg-emerald-600'}`}>
          {toast.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">TeckiA Assistant</h1>
          </div>
          <p className="text-gray-400 text-sm ml-1">Gestion des abonnements, OAuth Google et statistiques</p>
        </div>
        <button onClick={() => tab === 'users' ? loadUsers() : tab === 'oauth' ? loadOAuths() : loadStats()}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-sm transition-all border border-white/10">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-2 p-1 bg-black/30 rounded-2xl border border-white/8 w-fit">
        {[
          { key: 'users',  label: 'Utilisateurs', icon: Users,      badge: null },
          { key: 'oauth',  label: 'OAuth Google',  icon: Mail,       badge: pendingOAuths || null },
          { key: 'stats',  label: 'Statistiques',  icon: TrendingUp, badge: null },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
              ${tab === t.key ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            <t.icon className="w-4 h-4" />{t.label}
            {t.badge > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center animate-pulse">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB UTILISATEURS                                                      */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'users' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher par email ou userId…"
                className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-teal-500/50 transition-colors" />
            </div>
            <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)}
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-gray-300 focus:outline-none focus:border-teal-500/50 cursor-pointer">
              <option value="all">Tous les plans</option>
              <option value="pro">PRO seulement</option>
              <option value="free">Free seulement</option>
              <option value="disabled">Désactivés</option>
            </select>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total',      val: users.length,                         color: 'from-slate-700 to-slate-600',  icon: Users },
              { label: 'PRO',        val: users.filter(u => u.isPro).length,    color: 'from-teal-700 to-emerald-600', icon: Crown },
              { label: 'Free',       val: users.filter(u => !u.isPro).length,   color: 'from-slate-700 to-slate-600',  icon: User  },
              { label: 'Désactivés', val: users.filter(u => u.disabled).length, color: 'from-red-900 to-red-800',      icon: Ban   },
            ].map((s, i) => (
              <div key={i} className={`bg-gradient-to-br ${s.color} rounded-xl p-3 border border-white/10`}>
                <div className="flex items-center justify-between mb-1">
                  <s.icon className="w-4 h-4 text-white/60" />
                  <span className="text-2xl font-bold text-white">{s.val}</span>
                </div>
                <span className="text-xs text-white/60 font-medium">{s.label}</span>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <Database className="w-12 h-12 mb-3 opacity-40" />
              <p className="font-medium">Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map(u => (
                <div key={u.userId} className={`rounded-2xl border transition-all overflow-hidden
                  ${u.disabled ? 'border-red-900/40 bg-red-950/20' : u.isPro ? 'border-teal-800/40 bg-teal-950/10' : 'border-white/8 bg-white/3'}
                  hover:border-white/20`}>
                  <div className="flex items-center gap-3 p-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0
                      ${u.isPro ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white' : 'bg-white/10 text-gray-400'}`}>
                      {(u.userName || u.name || u.email || u.userId || '?')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-white truncate">
                          {u.userName || u.name || u.email?.split('@')[0] || u.userId?.slice(0,12) + '…'}
                        </span>
                        <PlanBadge isPro={u.isPro} disabled={u.disabled} />
                        {!u.hasSub && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-800 text-gray-500 border border-gray-700">Pas encore actif</span>
                        )}
                        {u.gmailConnected && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-500/15 text-blue-400 border border-blue-500/25">
                            <Mail className="w-3 h-3" /> {u.gmailEmail || 'Gmail'}
                          </span>
                        )}
                        {u.calendarConnected && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-500/15 text-purple-400 border border-purple-500/25">
                            <Calendar className="w-3 h-3" /> Calendar
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-500">{u.msgCountToday || 0} msg/auj</span>
                        {u.expiresAt && <span className="text-xs text-amber-400">Expire : {fmtShort(u.expiresAt)}</span>}
                        <span className="text-xs text-gray-600">{fmtShort(u.updatedAt)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setPro(u.userId, !u.isPro)} disabled={u.disabled} title={u.isPro ? 'Repasser en Free' : 'Passer en PRO'}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all
                          ${u.isPro ? 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 border border-teal-500/30' : 'bg-gray-700 text-gray-400 hover:bg-teal-600 hover:text-white border border-gray-600'}
                          ${u.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                        <Crown className="w-3 h-3" />{u.isPro ? 'PRO ✓' : '→ PRO'}
                      </button>
                      <button onClick={() => setDisabled(u.userId, !u.disabled)} title={u.disabled ? 'Réactiver' : 'Désactiver'}
                        className={`p-1.5 rounded-lg transition-all border
                          ${u.disabled ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25' : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'}`}>
                        {u.disabled ? <ToggleLeft className="w-4 h-4" /> : <ToggleRight className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setExpanded(expanded === u.userId ? null : u.userId)}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 transition-all border border-white/8">
                        {expanded === u.userId ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {expanded === u.userId && (
                    <div className="border-t border-white/8 bg-black/20 p-4 space-y-4">

                      {/* Stats principales */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: 'Msg aujourd"hui', val: u.msgCountToday || 0, max: u.isPro ? '∞' : '20', color: 'text-teal-400',   sub: 'messages' },
                          { label: 'Total messages',   val: u.totalMsgs     || 0, max: null,                  color: 'text-blue-400',   sub: 'all-time' },
                          { label: 'Moy/jour',         val: u.avgMsgsPerDay || 0, max: null,                  color: 'text-purple-400', sub: `sur ${u.daysActive || 1}j` },
                          { label: 'Tâches',           val: u.tasksCount    || 0, max: null,                  color: 'text-amber-400',  sub: 'créées' },
                        ].map((s, i) => (
                          <div key={i} className="bg-white/5 rounded-xl p-3 border border-white/8">
                            <div className={`text-xl font-black ${s.color} mb-0.5`}>
                              {s.val}{s.max ? <span className="text-xs text-gray-500">/{s.max}</span> : ''}
                            </div>
                            <div className="text-xs text-white/70 font-semibold">{s.label}</div>
                            <div className="text-xs text-gray-600">{s.sub}</div>
                          </div>
                        ))}
                      </div>

                      {/* Infos compte */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        {(u.userName || u.name) && (
                          <div className="flex items-center gap-2 p-2 bg-teal-500/5 rounded-lg border border-teal-500/15">
                            <span className="text-gray-500 w-24 flex-shrink-0">Nom:</span>
                            <span className="text-white font-bold">{u.userName || u.name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 p-2 bg-white/3 rounded-lg border border-white/8">
                          <span className="text-gray-500 font-mono w-24 flex-shrink-0">userId:</span>
                          <code className="text-teal-400 font-mono truncate">{u.userId}</code>
                        </div>
                        {u.accountCreated && (
                          <div className="flex items-center gap-2 p-2 bg-white/3 rounded-lg border border-white/8">
                            <span className="text-gray-500 w-24 flex-shrink-0">Inscrit le:</span>
                            <span className="text-white">{fmt(u.accountCreated)}</span>
                          </div>
                        )}
                        {u.isPro && u.proSince && (
                          <div className="flex items-center gap-2 p-2 bg-teal-500/10 rounded-lg border border-teal-500/20">
                            <Crown className="w-3 h-3 text-teal-400 flex-shrink-0" />
                            <span className="text-gray-500 w-20 flex-shrink-0">Pro depuis:</span>
                            <span className="text-teal-400 font-semibold">{fmt(u.proSince)}</span>
                          </div>
                        )}
                        {u.expiresAt && (
                          <div className="flex items-center gap-2 p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                            <Clock className="w-3 h-3 text-amber-400 flex-shrink-0" />
                            <span className="text-gray-500 w-20 flex-shrink-0">Expire:</span>
                            <span className="text-amber-400 font-semibold">{fmt(u.expiresAt)}</span>
                          </div>
                        )}
                        {u.msgDate && (
                          <div className="flex items-center gap-2 p-2 bg-white/3 rounded-lg border border-white/8">
                            <span className="text-gray-500 w-24 flex-shrink-0">Actif le:</span>
                            <span className="text-white">{u.msgDate}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 p-2 bg-white/3 rounded-lg border border-white/8">
                          <span className="text-gray-500 w-24 flex-shrink-0">Sessions:</span>
                          <span className="text-white font-semibold">{u.sessionsCount || 0}</span>
                        </div>
                      </div>

                      {/* Barre consommation */}
                      {!u.isPro && (
                        <div>
                          <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>Consommation aujourd'hui</span>
                            <span className={u.msgCountToday >= 20 ? 'text-red-400 font-bold' : 'text-teal-400'}>{u.msgCountToday || 0}/20</span>
                          </div>
                          <div className="h-2 bg-white/8 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${u.msgCountToday >= 20 ? 'bg-red-500' : u.msgCountToday >= 15 ? 'bg-amber-500' : 'bg-teal-500'}`}
                              style={{ width: `${Math.min(100, ((u.msgCountToday||0)/20)*100)}%` }}/>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 pt-1 border-t border-white/8">
                        <button onClick={() => setPro(u.userId, true)} disabled={u.isPro || u.disabled}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 hover:bg-teal-500 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                          <Crown className="w-3 h-3" /> Activer PRO
                        </button>
                        <button onClick={() => setPro(u.userId, false)} disabled={!u.isPro || u.disabled}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-700 hover:bg-gray-600 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                          Repasser Free
                        </button>
                        <button onClick={() => setDisabled(u.userId, true)} disabled={u.disabled}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-800 hover:bg-red-700 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                          <Ban className="w-3 h-3" /> Désactiver
                        </button>
                        <button onClick={() => setDisabled(u.userId, false)} disabled={!u.disabled}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-800 hover:bg-emerald-700 text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                          <CheckCircle className="w-3 h-3" /> Réactiver
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB OAUTH GOOGLE                                                      */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'oauth' && (
        <div className="space-y-4">
          {pendingOAuths > 0 && (
            <div className="flex items-center gap-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-300 font-bold text-sm">{pendingOAuths} demande{pendingOAuths > 1 ? 's' : ''} en attente d'approbation</p>
                <p className="text-amber-400/60 text-xs mt-0.5">Approuve pour permettre la connexion Google OAuth</p>
              </div>
            </div>
          )}

          <div className="p-4 rounded-2xl bg-blue-500/8 border border-blue-500/20">
            <p className="text-blue-300 text-xs font-semibold mb-2 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" /> PROCESSUS D'APPROBATION
            </p>
            <ol className="text-blue-300/70 text-xs space-y-1 list-decimal list-inside">
              <li>L'utilisateur soumet son email depuis l'app TeckiA Mobile</li>
              <li>Ajouter l'email dans <strong className="text-blue-300">Google Cloud Console → OAuth Consent Screen → Test Users</strong></li>
              <li>Cliquer <strong className="text-blue-300">Approuver</strong> ici — l'URL OAuth sera générée automatiquement</li>
              <li>L'utilisateur reçoit le bouton de connexion dans l'app</li>
            </ol>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : oauths.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <Mail className="w-12 h-12 mb-3 opacity-40" />
              <p className="font-medium">Aucune demande OAuth</p>
            </div>
          ) : (
            <div className="space-y-2">
              {oauths.map(o => (
                <div key={o.$id} className={`rounded-2xl border p-4 transition-all
                  ${o.status === 'pending'   ? 'border-amber-500/30 bg-amber-950/10'    :
                    o.status === 'connected' ? 'border-emerald-500/20 bg-emerald-950/5' :
                    o.status === 'approved'  ? 'border-blue-500/20 bg-blue-950/5'       :
                    'border-white/8 bg-white/3'}`}>
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                      {(o.email || '?')[0].toUpperCase()}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-sm font-bold text-white">{o.email}</span>
                        <OAuthBadge status={o.status} />
                        <span className="text-xs text-gray-500 px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                          {o.mode === 'both' ? 'Gmail + Calendar' : o.mode === 'email' ? 'Gmail' : 'Calendar'}
                        </span>
                      </div>

                      {/* ── Nom + userId ── */}
                      <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap mb-1">
                        {/* Nom réel depuis user_limits.userName */}
                        {o.userName && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-500/15 border border-teal-500/30">
                            <User className="w-3 h-3 text-teal-400" />
                            <span className="text-teal-300 font-bold text-xs">{o.userName}</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <span className="text-gray-600">userId:</span>
                          <code className="text-teal-400 font-mono text-xs">{o.userId?.slice(0,12)}…</code>
                        </span>
                        <span>·</span>
                        <span>{fmt(o.createdAt)}</span>
                      </div>

                      {o.status === 'connected' && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-400">
                          <CheckCircle className="w-3 h-3" /> Compte Google connecté avec succès
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Pending → Approuver / Rejeter */}
                      {o.status === 'pending' && (
                        <>
                          <button onClick={() => approveOAuth(o.$id, 'approve')}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-all">
                            <Check className="w-3 h-3" /> Approuver
                          </button>
                          <button onClick={() => approveOAuth(o.$id, 'reject')}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-red-800 hover:bg-red-700 text-white transition-all">
                            <X className="w-3 h-3" /> Rejeter
                          </button>
                        </>
                      )}

                      {/* Approved → En attente */}
                      {o.status === 'approved' && (
                        <span className="text-xs text-blue-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> En attente connexion
                        </span>
                      )}

                      {/* Connected → Dissocier */}
                      {o.status === 'connected' && (
                        <button
                          onClick={() => dissociateGoogle(o)}
                          disabled={dissociating === o.$id}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-red-900/60 hover:bg-red-800 text-red-300 border border-red-700/40 transition-all disabled:opacity-50"
                          title="Dissocier le compte Google">
                          {dissociating === o.$id
                            ? <RefreshCw className="w-3 h-3 animate-spin" />
                            : <Unlink className="w-3 h-3" />
                          }
                          Dissocier
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB STATISTIQUES                                                      */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {tab === 'stats' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
            </div>
          ) : globalStats ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Utilisateurs actifs', val: globalStats.activeUsers    || 0, sub: 'Dernières 24h',                                    icon: Activity,     color: 'from-teal-600 to-emerald-600'  },
                  { label: 'Messages IA/jour',    val: globalStats.totalMsgsToday || 0, sub: 'Tous les users',                                    icon: MessageSquare, color: 'from-blue-600 to-cyan-600'    },
                  { label: 'Comptes Pro',         val: globalStats.proUsers       || 0, sub: `/ ${globalStats.totalUsers || 0} total`,            icon: Crown,        color: 'from-amber-600 to-orange-600'  },
                  { label: 'Gmail connectés',     val: globalStats.gmailConnected || 0, sub: 'OAuth actifs',                                      icon: Mail,         color: 'from-purple-600 to-pink-600'   },
                ].map((s, i) => (
                  <div key={i} className={`bg-gradient-to-br ${s.color} rounded-2xl p-5 text-white shadow-lg`}>
                    <div className="flex items-center justify-between mb-3">
                      <s.icon className="w-6 h-6 opacity-80" />
                      <span className="text-3xl font-bold">{s.val}</span>
                    </div>
                    <p className="font-semibold text-sm mb-0.5">{s.label}</p>
                    <p className="text-white/60 text-xs">{s.sub}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white/3 rounded-2xl border border-white/8 p-5">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-teal-400" /> Taux de conversion Free → Pro
                </h3>
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Free</span><span>{globalStats.totalUsers - globalStats.proUsers || 0} users</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gray-600 rounded-full" style={{ width: `${globalStats.totalUsers ? ((globalStats.totalUsers - (globalStats.proUsers || 0)) / globalStats.totalUsers * 100).toFixed(0) : 0}%` }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-gray-500">
                    {globalStats.totalUsers ? (((globalStats.totalUsers - (globalStats.proUsers || 0)) / globalStats.totalUsers) * 100).toFixed(0) : 0}%
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Pro</span><span>{globalStats.proUsers || 0} users</span>
                    </div>
                    <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full" style={{ width: `${globalStats.totalUsers ? ((globalStats.proUsers || 0) / globalStats.totalUsers * 100).toFixed(0) : 0}%` }} />
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-teal-400">
                    {globalStats.totalUsers ? (((globalStats.proUsers || 0) / globalStats.totalUsers) * 100).toFixed(0) : 0}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Msgs/user/jour (moy.)',  val: globalStats.avgMsgsPerUser || 0, icon: MessageSquare, color: 'text-teal-400'  },
                  { label: 'Demandes OAuth totales', val: globalStats.totalOAuthReqs || 0, icon: Shield,        color: 'text-blue-400'  },
                  { label: 'Tâches créées (total)',  val: globalStats.totalTasks     || 0, icon: Zap,           color: 'text-amber-400' },
                ].map((s, i) => (
                  <div key={i} className="bg-white/3 rounded-2xl border border-white/8 p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                      <s.icon className={`w-6 h-6 ${s.color}`} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-white">{s.val}</p>
                      <p className="text-xs text-gray-500">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {globalStats.topUsers?.length > 0 && (
                <div className="bg-white/3 rounded-2xl border border-white/8 p-5">
                  <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-teal-400" /> Top utilisateurs actifs (auj.)
                  </h3>
                  <div className="space-y-2">
                    {globalStats.topUsers.map((u, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-white/5 transition-colors">
                        <span className="text-gray-600 text-xs font-mono w-4">{i + 1}</span>
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-600 to-emerald-600 flex items-center justify-center text-xs font-bold text-white">
                          {(u.email || '?')[0].toUpperCase()}
                        </div>
                        <span className="text-sm text-gray-300 flex-1 truncate">{u.email}</span>
                        <span className="text-xs font-bold text-teal-400">{u.msgCountToday} msg</span>
                        {u.isPro && <Crown className="w-3.5 h-3.5 text-amber-400" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <TrendingUp className="w-12 h-12 mb-3 opacity-40" />
              <p className="font-medium">Chargement des statistiques…</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}